import {
  getList,
  getString,
  getOptionalString,
  getLeoDate,
  LeoDate,
  getOptionalLeoDate,
  getObject,
  getInt32,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { GetBOUsersSortOrder, GetBOUsersSortOrderDTO, GetBOUsersSortOrderEnums } from "./getBoUsersSortOrder";
import { BoUserManagementItemsPerPage, BoUserManagementItemsPerPageDTO } from "./boUserManagementItemsPerPage";
import { BoUserManagementPageIndex, BoUserManagementPageIndexDTO } from "./boUserManagementPageIndex";
import { GetBOUsersPaginationResponse, GetBOUsersPaginationResponseDTO } from "./getBoUsersPaginationResponse";
/* eslint-disable import/export */
export abstract class GetBOUsersRPC implements
  LeoRPC<GetBOUsersRPC.Request, GetBOUsersRPC.Response, GetBOUsersRPC.Errors.Errors>
{
  abstract execute(
    request: GetBOUsersRPC.Request
  ): Promise<LeoRPCResult<GetBOUsersRPC.Response, GetBOUsersRPC.Errors.Errors>>;
}

export namespace GetBOUsersRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    sortList: GetBOUsersSortOrderDTO[];
    firstName?: string | null;
    lastName?: string | null;
    emailAddress?: string | null;
    role?: string | null;
    addedOnStartDate?: string | null;
    addedOnEndDate?: string | null;
    itemsPerPage: BoUserManagementItemsPerPageDTO;
    pageIndex: BoUserManagementPageIndexDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetBOUsersRPCRequest: boolean = true;
  
    readonly sortList: GetBOUsersSortOrder[];
    readonly firstName: string | null;
    readonly lastName: string | null;
    readonly emailAddress: string | null;
    readonly role: string | null;
    readonly addedOnStartDate: LeoDate | null;
    readonly addedOnEndDate: LeoDate | null;
    readonly itemsPerPage: BoUserManagementItemsPerPage;
    readonly pageIndex: BoUserManagementPageIndex;
  
    static readonly constraints = {
      "firstName": {
        "minSize": 1,
        "maxSize": 200,
        
        
        
        
      },
      "lastName": {
        "minSize": 1,
        "maxSize": 200,
        
        
        
        
      },
      "emailAddress": {
        "minSize": 1,
        "maxSize": 200,
        
        
        
        
      },
      "role": {
        "minSize": 1,
        "maxSize": 200,
        
        
        
        
      },
    }
  
    constructor(
    sortList: GetBOUsersSortOrder[], 
    firstName: string | null = null, 
    lastName: string | null = null, 
    emailAddress: string | null = null, 
    role: string | null = null, 
    addedOnStartDate: LeoDate | null = null, 
    addedOnEndDate: LeoDate | null = null, 
    itemsPerPage: BoUserManagementItemsPerPage, 
    pageIndex: BoUserManagementPageIndex
    ) {
      this.sortList = sortList;
      if ( firstName !== undefined && firstName !== null) {
        Request.validateFirstName(firstName);
      }
      this.firstName = firstName;
      if ( lastName !== undefined && lastName !== null) {
        Request.validateLastName(lastName);
      }
      this.lastName = lastName;
      if ( emailAddress !== undefined && emailAddress !== null) {
        Request.validateEmailAddress(emailAddress);
      }
      this.emailAddress = emailAddress;
      if ( role !== undefined && role !== null) {
        Request.validateRole(role);
      }
      this.role = role;
      this.addedOnStartDate = addedOnStartDate;
      this.addedOnEndDate = addedOnEndDate;
      this.itemsPerPage = itemsPerPage;
      this.pageIndex = pageIndex;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const sortList = getList<GetBOUsersSortOrderDTO, GetBOUsersSortOrder>(
        dto,
        "sortList",
        GetBOUsersSortOrder.fromDTO,
        InvalidRequestSortListError
      );
      const firstName = getOptionalString(dto, "firstName", InvalidRequestFirstNameError);
      const lastName = getOptionalString(dto, "lastName", InvalidRequestLastNameError);
      const emailAddress = getOptionalString(dto, "emailAddress", InvalidRequestEmailAddressError);
      const role = getOptionalString(dto, "role", InvalidRequestRoleError);
      const addedOnStartDate = getOptionalLeoDate(dto, "addedOnStartDate", InvalidRequestAddedOnStartDateError);
      const addedOnEndDate = getOptionalLeoDate(dto, "addedOnEndDate", InvalidRequestAddedOnEndDateError);
      const itemsPerPage = BoUserManagementItemsPerPage.fromDTO(getObject(dto, "itemsPerPage", InvalidRequestItemsPerPageError));
      const pageIndex = BoUserManagementPageIndex.fromDTO(getObject(dto, "pageIndex", InvalidRequestPageIndexError));
      return new Request(
        sortList,
        firstName,
        lastName,
        emailAddress,
        role,
        addedOnStartDate,
        addedOnEndDate,
        itemsPerPage,
        pageIndex
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          sortList: this.sortList.map((e: GetBOUsersSortOrder) => { return e.toDTO() }),
          itemsPerPage: this.itemsPerPage.toDTO(),
          pageIndex: this.pageIndex.toDTO()
      };
      if (this.firstName !== undefined && this.firstName !== null){
        dto.firstName = this.firstName;
      }
      if (this.lastName !== undefined && this.lastName !== null){
        dto.lastName = this.lastName;
      }
      if (this.emailAddress !== undefined && this.emailAddress !== null){
        dto.emailAddress = this.emailAddress;
      }
      if (this.role !== undefined && this.role !== null){
        dto.role = this.role;
      }
      if (this.addedOnStartDate !== undefined && this.addedOnStartDate !== null){
        dto.addedOnStartDate = this.addedOnStartDate.toDTO();
      }
      if (this.addedOnEndDate !== undefined && this.addedOnEndDate !== null){
        dto.addedOnEndDate = this.addedOnEndDate.toDTO();
      }
      return dto;
    }
    private static validateFirstName = (firstName: string): void => {
      if(firstName.length < 1) {
        throw new InvalidRequestFirstNameMinSizeError(
          1,
          `Attribute firstName is too short. Size is ${firstName.length}. Min size is 1.`
        );
      }
      if(firstName.length > 200) {
        throw new InvalidRequestFirstNameMaxSizeError(
          200,
          `Attribute firstName is too long. Size is ${firstName.length}. Max size is 200.`
        );
      }
        if(firstName.trim().length === 0) {
          throw new InvalidRequestFirstNameNonEmptyError(
            `Attribute firstName cannot be an empty string.`
          );
        }
    }
    private static validateLastName = (lastName: string): void => {
      if(lastName.length < 1) {
        throw new InvalidRequestLastNameMinSizeError(
          1,
          `Attribute lastName is too short. Size is ${lastName.length}. Min size is 1.`
        );
      }
      if(lastName.length > 200) {
        throw new InvalidRequestLastNameMaxSizeError(
          200,
          `Attribute lastName is too long. Size is ${lastName.length}. Max size is 200.`
        );
      }
        if(lastName.trim().length === 0) {
          throw new InvalidRequestLastNameNonEmptyError(
            `Attribute lastName cannot be an empty string.`
          );
        }
    }
    private static validateEmailAddress = (emailAddress: string): void => {
      if(emailAddress.length < 1) {
        throw new InvalidRequestEmailAddressMinSizeError(
          1,
          `Attribute emailAddress is too short. Size is ${emailAddress.length}. Min size is 1.`
        );
      }
      if(emailAddress.length > 200) {
        throw new InvalidRequestEmailAddressMaxSizeError(
          200,
          `Attribute emailAddress is too long. Size is ${emailAddress.length}. Max size is 200.`
        );
      }
        if(emailAddress.trim().length === 0) {
          throw new InvalidRequestEmailAddressNonEmptyError(
            `Attribute emailAddress cannot be an empty string.`
          );
        }
    }
    private static validateRole = (role: string): void => {
      if(role.length < 1) {
        throw new InvalidRequestRoleMinSizeError(
          1,
          `Attribute role is too short. Size is ${role.length}. Min size is 1.`
        );
      }
      if(role.length > 200) {
        throw new InvalidRequestRoleMaxSizeError(
          200,
          `Attribute role is too long. Size is ${role.length}. Max size is 200.`
        );
      }
        if(role.trim().length === 0) {
          throw new InvalidRequestRoleNonEmptyError(
            `Attribute role cannot be an empty string.`
          );
        }
    }
    
  }
  
  Request.prototype.toString = function toString(): string {
    return `GetBOUsersRPC(sortList='${this.sortList}',firstName='${this.firstName}',lastName='${this.lastName}',emailAddress='${this.emailAddress}',role='${this.role}',addedOnStartDate='${this.addedOnStartDate}',addedOnEndDate='${this.addedOnEndDate}',itemsPerPage='${this.itemsPerPage}',pageIndex='${this.pageIndex}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    getBOUsersPaginationResponse: GetBOUsersPaginationResponseDTO[];
    totalItems: number;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetBOUsersResponse: boolean = true;
  
    readonly getBOUsersPaginationResponse: GetBOUsersPaginationResponse[];
    readonly totalItems: number;
  
  
    constructor(
    getBOUsersPaginationResponse: GetBOUsersPaginationResponse[], 
    totalItems: number
    ) {
      this.getBOUsersPaginationResponse = getBOUsersPaginationResponse;
      this.totalItems = totalItems;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const getBOUsersPaginationResponse = getList<GetBOUsersPaginationResponseDTO, GetBOUsersPaginationResponse>(
        dto,
        "getBOUsersPaginationResponse",
        GetBOUsersPaginationResponse.fromDTO,
        InvalidResponseGetBoUsersPaginationResponseError
      );
      const totalItems = getInt32(dto, "totalItems", InvalidResponseTotalItemsError);
      return new Response(
        getBOUsersPaginationResponse,
        totalItems
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          getBOUsersPaginationResponse: this.getBOUsersPaginationResponse.map((e: GetBOUsersPaginationResponse) => { return e.toDTO() }),
          totalItems: this.totalItems
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetBOUsers(getBOUsersPaginationResponse='${this.getBOUsersPaginationResponse}',totalItems='${this.totalItems}')`;
  };
  

  export enum RPCError {
    InvalidPageIndex = "INVALID_PAGE_INDEX",
    InvalidDateRange = "INVALID_DATE_RANGE"
  }

  export namespace Errors {
    
    export interface InvalidPageIndexDTO {
      code: string;
    }
    
    export class InvalidPageIndex extends LeoRPCError<RPCError> {
      readonly isInvalidPageIndex: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidPageIndex;
        super(code);
      }
    
    
      toDTO(): InvalidPageIndexDTO {
        const dto: InvalidPageIndexDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidPageIndex.prototype.toString = function toString(): string {
      return `GetBOUsersRPC.INVALID_PAGE_INDEX()`;
    };
    
    
    export interface InvalidDateRangeDTO {
      code: string;
    }
    
    export class InvalidDateRange extends LeoRPCError<RPCError> {
      readonly isInvalidDateRange: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidDateRange;
        super(code);
      }
    
    
      toDTO(): InvalidDateRangeDTO {
        const dto: InvalidDateRangeDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidDateRange.prototype.toString = function toString(): string {
      return `GetBOUsersRPC.INVALID_DATE_RANGE()`;
    };
    

    export type Errors = InvalidPageIndex | InvalidDateRange;
    export type ErrorsDTO = InvalidPageIndexDTO | InvalidDateRangeDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidPageIndex:
          return new InvalidPageIndex();
        case RPCError.InvalidDateRange:
          return new InvalidDateRange();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSortListError extends GetBOUsersRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSortListError";
  }
}

export class InvalidRequestFirstNameError extends GetBOUsersRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestFirstNameError";
  }
}

export class InvalidRequestFirstNameMaxSizeError extends InvalidRequestFirstNameError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestFirstNameMaxSizeError";
  }
}

export class InvalidRequestFirstNameMinSizeError extends InvalidRequestFirstNameError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestFirstNameMinSizeError";
  }
}

export class InvalidRequestFirstNameNonEmptyError extends InvalidRequestFirstNameError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestFirstNameNonEmptyError";
  }
}

export class InvalidRequestLastNameError extends GetBOUsersRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestLastNameError";
  }
}

export class InvalidRequestLastNameMaxSizeError extends InvalidRequestLastNameError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestLastNameMaxSizeError";
  }
}

export class InvalidRequestLastNameMinSizeError extends InvalidRequestLastNameError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestLastNameMinSizeError";
  }
}

export class InvalidRequestLastNameNonEmptyError extends InvalidRequestLastNameError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestLastNameNonEmptyError";
  }
}

export class InvalidRequestEmailAddressError extends GetBOUsersRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestEmailAddressError";
  }
}

export class InvalidRequestEmailAddressMaxSizeError extends InvalidRequestEmailAddressError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestEmailAddressMaxSizeError";
  }
}

export class InvalidRequestEmailAddressMinSizeError extends InvalidRequestEmailAddressError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestEmailAddressMinSizeError";
  }
}

export class InvalidRequestEmailAddressNonEmptyError extends InvalidRequestEmailAddressError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestEmailAddressNonEmptyError";
  }
}

export class InvalidRequestRoleError extends GetBOUsersRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestRoleError";
  }
}

export class InvalidRequestRoleMaxSizeError extends InvalidRequestRoleError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestRoleMaxSizeError";
  }
}

export class InvalidRequestRoleMinSizeError extends InvalidRequestRoleError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestRoleMinSizeError";
  }
}

export class InvalidRequestRoleNonEmptyError extends InvalidRequestRoleError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestRoleNonEmptyError";
  }
}

export class InvalidRequestAddedOnStartDateError extends GetBOUsersRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestAddedOnStartDateError";
  }
}

export class InvalidRequestAddedOnEndDateError extends GetBOUsersRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestAddedOnEndDateError";
  }
}

export class InvalidRequestItemsPerPageError extends GetBOUsersRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestItemsPerPageError";
  }
}

export class InvalidRequestPageIndexError extends GetBOUsersRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestPageIndexError";
  }
}

export class InvalidResponseGetBoUsersPaginationResponseError extends GetBOUsersRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseGetBoUsersPaginationResponseError";
  }
}

export class InvalidResponseTotalItemsError extends GetBOUsersRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseTotalItemsError";
  }
}

export class InvalidResponseInvalidPageIndexError extends GetBOUsersRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidPageIndexError";
  }
}

export class InvalidResponseInvalidDateRangeError extends GetBOUsersRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidDateRangeError";
  }
}

